import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Pillar, TrackerGridInput, TrackerGridOutput, TrackerKpi } from 'chery-contracts';
import { applicableTarget, weeksBetween, yearToDateWeekIds } from 'chery-kpi-engine';
import { CalendarDays } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Notice, type NoticeTone } from '../../components/ui/Notice';
import { Spinner } from '../../components/ui/Spinner';
import { downloadText } from '../../lib/csv';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { canManageKpis } from '../kpis/kpi-display';
import { CommentPanel } from './CommentPanel';
import { buildTrackerSummaryOf, buildTrackerValueAt, draftFromInput, trackerCalcDefs } from './tracker-calc';
import { buildTrackerCsv, trackerCsvFileName } from './tracker-csv';
import { baseRecord, childrenByParent, conventionExample, editAccessNote, matchesSearch, selectionTotalText, weekDateLabel } from './tracker-display';
import { cellKey, selectionSize, selectionTotal, type CellValue } from './tracker-state';
import { TrackerGrid, type PillarGroup } from './TrackerGrid';
import { TrackerToolbar, type TrackerRange } from './TrackerToolbar';
import { useTrackerEditing, type CellRef } from './use-tracker-editing';

interface PageNotice {
  tone: NoticeTone;
  text: string;
}

type PendingDialog = { kind: 'discard-then'; action: () => void } | { kind: 'cancel' } | { kind: 'clear-value'; ref: CellRef } | null;

const toGridInput = (range: TrackerRange): TrackerGridInput =>
  range.mode === 'all' ? { all: true } : { fromWeekId: range.fromWeekId, toWeekId: range.toWeekId };

/**
 * The weekly tracker (plan §3.1, §7). The server decides what the caller may see and edit (`tracker.grid` is
 * pillar-scoped and flags each KPI `editable`); this page stages edits in the browser, computes calculated cells and
 * Total / Average / Latest over them with the shared engine — the text still being typed included — and saves them
 * in one call. Year to date is the default range.
 */
export const TrackerPage = () => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [range, setRange] = useState<TrackerRange | null>(null);
  const [lastCustomRange, setLastCustomRange] = useState<TrackerRange | null>(null);
  const [rangeWarning, setRangeWarning] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [pillarFilter, setPillarFilter] = useState<Pillar | ''>('');
  const [showComments, setShowComments] = useState(true);
  const [collapsed, setCollapsed] = useState<ReadonlySet<Pillar>>(new Set());
  const [notice, setNotice] = useState<PageNotice | null>(null);
  const [dialog, setDialog] = useState<PendingDialog>(null);
  const [commentRef, setCommentRef] = useState<CellRef | null>(null);

  const weeksQuery = useQuery(orpc.weeks.list.queryOptions({ input: {} }));
  const allWeeks = useMemo(() => weeksQuery.data?.items ?? [], [weeksQuery.data]);

  const yearToDate = useCallback((): TrackerRange | null => {
    const ids = yearToDateWeekIds(allWeeks, new Date());

    return ids ? { mode: 'range', fromWeekId: ids.fromId, toWeekId: ids.toId } : null;
  }, [allWeeks]);

  // Year to date is the default range, resolved once the weeks are known.
  useEffect(() => {
    if (range === null && allWeeks.length > 0) {
      setRange(yearToDate());
    }
  }, [range, allWeeks, yearToDate]);

  const gridInput = range ? toGridInput(range) : null;
  const gridQuery = useQuery({
    ...orpc.tracker.grid.queryOptions({ input: gridInput ?? { all: true } }),
    enabled: gridInput !== null,
    placeholderData: keepPreviousData,
  });
  const grid = gridQuery.data;

  const kpiById = useMemo(() => new Map((grid?.kpis ?? []).map((kpi) => [kpi.id, kpi])), [grid]);
  const baseOf = useCallback(
    (kpiId: string, weekId: string) => {
      const kpi = kpiById.get(kpiId);

      return kpi ? baseRecord(kpi, grid?.values[cellKey(kpiId, weekId)]) : null;
    },
    [grid, kpiById],
  );

  const filteredKpis = useMemo(
    () => (grid?.kpis ?? []).filter((kpi) => (pillarFilter === '' || kpi.pillar === pillarFilter) && matchesSearch(kpi, search)),
    [grid, pillarFilter, search],
  );

  const groups: PillarGroup[] = useMemo(
    () =>
      (grid?.pillars ?? [])
        .filter(({ pillar }) => pillarFilter === '' || pillar === pillarFilter)
        .map(({ pillar, access }) => ({
          pillar,
          access,
          kpis: filteredKpis.filter((kpi) => kpi.pillar === pillar),
          collapsed: collapsed.has(pillar),
        }))
        .filter((group) => group.kpis.length > 0),
    [grid, pillarFilter, filteredKpis, collapsed],
  );

  const childrenOf = useMemo(() => childrenByParent(grid?.kpis ?? []), [grid]);
  const nameOf = useCallback((kpiId: string) => kpiById.get(kpiId)?.name ?? null, [kpiById]);
  const navigableRows = useMemo(() => groups.filter((group) => !group.collapsed).flatMap((group) => group.kpis), [groups]);
  const weeks = useMemo(() => grid?.weeks ?? [], [grid]);
  const canEdit = (grid?.pillars ?? []).some((each) => each.access === 'EDIT');

  const onMessage = useCallback((text: string) => setNotice({ tone: 'info', text }), []);
  const onOpenComment = useCallback((kpiId: string, weekId: string) => setCommentRef({ kpiId, weekId }), []);
  const editing = useTrackerEditing({ rows: navigableRows, weeks, baseOf, keyboardEnabled: dialog === null, onMessage });
  const { unsavedCount, recordAt, draftInput } = editing;

  // Every number on screen goes through one engine resolver: saved cells, staged edits and the open editor's text.
  const calcDefs = useMemo(() => trackerCalcDefs(grid?.kpis ?? [], grid?.calcDefs ?? []), [grid]);
  const draft = useMemo(() => {
    const kpi = draftInput ? kpiById.get(draftInput.kpiId) : undefined;

    return draftInput && kpi ? draftFromInput(kpi, draftInput.weekId, draftInput.raw) : null;
  }, [draftInput, kpiById]);
  const valueAt = useMemo(
    () =>
      buildTrackerValueAt({
        defs: calcDefs,
        shownValueAt: (kpiId, weekId) => recordAt(kpiId, weekId).value,
        isShown: (kpiId) => kpiById.has(kpiId),
        stored: grid?.values ?? {},
        draft,
      }),
    [calcDefs, recordAt, kpiById, grid, draft],
  );

  const weekIds = useMemo(() => weeks.map((week) => week.id), [weeks]);
  const summaryOf = useMemo(() => buildTrackerSummaryOf(weekIds, valueAt), [weekIds, valueAt]);
  const toWeekDate = weeks[weeks.length - 1]?.weekDate ?? null;

  // Leaving with unsaved edits: in-app navigation asks first; closing or reloading the tab gets the browser prompt.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => unsavedCount > 0 && currentLocation.pathname !== nextLocation.pathname);

  useEffect(() => {
    if (unsavedCount === 0) {
      return;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [unsavedCount]);

  // A new range opens scrolled to its end — the current week when it is in range — not to January.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolledFor = useRef<string | null>(null);
  const rangeKey = gridInput && grid ? JSON.stringify(gridInput) : null;

  useEffect(() => {
    const container = scrollRef.current;

    if (!container || rangeKey === null || scrolledFor.current === rangeKey || gridQuery.isPlaceholderData) {
      return;
    }

    scrolledFor.current = rangeKey;

    const current = container.querySelector<HTMLElement>('th[data-current-week]');

    // Mid-range (All weeks), bring the current week to the right edge; otherwise show the end, totals included.
    if (current && current.nextElementSibling?.hasAttribute('data-week-id')) {
      container.scrollLeft = Math.max(0, current.offsetLeft + current.offsetWidth - container.clientWidth);
    } else {
      container.scrollLeft = container.scrollWidth;
    }
  }, [rangeKey, gridQuery.isPlaceholderData]);

  const save = useMutation(orpc.tracker.saveValues.mutationOptions());
  const addWeek = useMutation(orpc.weeks.add.mutationOptions());

  /** Runs `action` at once, or after the user agrees to discard their unsaved edits. */
  const guarded = (action: () => void): void => {
    if (unsavedCount > 0) {
      setDialog({ kind: 'discard-then', action });
    } else {
      action();
    }
  };

  const applyRange = (next: TrackerRange | null): void => {
    setRangeWarning(null);
    setCommentRef(null);
    editing.clearSelection();
    setRange(next);

    if (next?.mode === 'range') {
      setLastCustomRange(next);
    }
  };

  const onRangeChange = (fromWeekId: string, toWeekId: string): void => {
    const checked = weeksBetween(allWeeks, fromWeekId, toWeekId);

    // Refused, never swapped or shortened: the selects stay on the last valid range.
    if (!checked.ok) {
      setRangeWarning(checked.message);

      return;
    }

    guarded(() => applyRange({ mode: 'range', fromWeekId, toWeekId }));
  };

  const doSave = async (): Promise<boolean> => {
    if (editing.invalidCount > 0) {
      setNotice({ tone: 'danger', text: t('tracker.invalidBeforeSave', { count: editing.invalidCount }) });

      return false;
    }

    const edits = editing.savePayload();

    if (edits.length === 0 || !gridInput) {
      return true;
    }

    try {
      const result = await save.mutateAsync({ edits });

      queryClient.setQueryData(orpc.tracker.grid.queryKey({ input: gridInput }), (current: TrackerGridOutput | undefined) => {
        if (!current) {
          return current;
        }

        const values = { ...current.values };

        for (const edit of edits) {
          const key = cellKey(edit.kpiId, edit.weekId);
          const stored = result.values[key];

          if (stored) {
            values[key] = stored;
          } else {
            delete values[key];
          }
        }

        return { ...current, values };
      });
      editing.discardAll();
      void queryClient.invalidateQueries({ queryKey: orpc.tracker.key() });
      setNotice({ tone: 'success', text: t('tracker.saved', { count: edits.length }) });

      return true;
    } catch (error) {
      setNotice({ tone: 'danger', text: serverMessage(error) ?? t('errors.saveFailed') });

      return false;
    }
  };

  const onAddWeek = async (): Promise<void> => {
    try {
      const week = await addWeek.mutateAsync({});

      await queryClient.invalidateQueries({ queryKey: orpc.weeks.key() });
      setNotice({ tone: 'success', text: t('tracker.weekAdded', { week: weekDateLabel(week.weekDate) }) });
    } catch (error) {
      setNotice({ tone: 'danger', text: serverMessage(error) ?? t('errors.saveFailed') });
    }
  };

  const onExport = (): void => {
    if (editing.invalidCount > 0) {
      setNotice({ tone: 'danger', text: t('tracker.invalidBeforeExport', { count: editing.invalidCount }) });

      return;
    }

    // Exactly what is shown: the filtered KPIs (collapsed pillars included), the range's weeks, staged edits applied.
    const kpis: TrackerKpi[] = groups.flatMap((group) => group.kpis);

    downloadText(
      trackerCsvFileName(weeks),
      buildTrackerCsv({
        kpis,
        weeks,
        valueAt,
        summaryOf,
        targetOf: (kpiId) => (toWeekDate === null ? null : (applicableTarget(grid?.targets[kpiId] ?? [], toWeekDate)?.value ?? null)),
      }),
    );
    setNotice({ tone: 'success', text: t('tracker.exported', { kpis: kpis.length, weeks: weeks.length }) });
  };

  const { bounds } = editing;
  const selectionValues = useMemo(() => {
    if (!bounds) {
      return [];
    }

    const values: CellValue[] = [];

    for (let row = bounds.top; row <= bounds.bottom; row += 1) {
      for (let col = bounds.left; col <= bounds.right; col += 1) {
        const kpi = navigableRows[row];
        const week = weeks[col];

        if (kpi && week) {
          values.push(valueAt(kpi.id, week.id));
        }
      }
    }

    return values;
  }, [bounds, navigableRows, weeks, valueAt]);

  const exampleWeek = allWeeks.find((week) => week.id === (grid?.currentWeekId ?? weeksQuery.data?.currentWeekId)) ?? weeks[weeks.length - 1];
  const example = exampleWeek ? conventionExample(exampleWeek.weekDate) : null;

  const commentKpi = commentRef ? kpiById.get(commentRef.kpiId) : undefined;
  const commentWeek = commentRef ? weeks.find((week) => week.id === commentRef.weekId) : undefined;

  if (weeksQuery.isPending || (range !== null && gridQuery.isPending)) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  if (weeksQuery.isError || allWeeks.length === 0) {
    return (
      <div className="p-6">
        <Notice tone="danger">{weeksQuery.isError ? t('errors.loadFailed') : t('tracker.noWeeks')}</Notice>
      </div>
    );
  }

  const shownRange: TrackerRange = range ?? { mode: 'all' };
  const total = selectionTotal(selectionValues);
  const cellsSelected = selectionSize(bounds);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TrackerToolbar
        allWeeks={allWeeks}
        range={shownRange}
        shownWeekCount={weeks.length}
        onRangeChange={onRangeChange}
        onToggleAllWeeks={() => guarded(() => applyRange(shownRange.mode === 'all' ? (lastCustomRange ?? yearToDate()) : { mode: 'all' }))}
        onYearToDate={() => guarded(() => applyRange(yearToDate()))}
        pillars={grid?.pillars ?? []}
        pillarFilter={pillarFilter}
        onPillarFilter={setPillarFilter}
        search={search}
        onSearch={setSearch}
        showComments={showComments}
        onToggleComments={() => setShowComments((shown) => !shown)}
        canComment={editing.focus !== null}
        onComment={() => setCommentRef(editing.focus)}
        canAddWeek={canManageKpis(user?.role)}
        isAddingWeek={addWeek.isPending}
        onAddWeek={() => void onAddWeek()}
        onExport={onExport}
        onOpenReports={() => navigate('/reports')}
        canEdit={canEdit}
        canUndo={editing.canUndo}
        unsavedCount={unsavedCount}
        isSaving={save.isPending}
        onUndo={editing.undo}
        onCancel={() => setDialog({ kind: 'cancel' })}
        onSave={() => void doSave()}
      />

      <div
        data-week-convention
        className="flex items-center gap-2 border-b border-border bg-muted/50 px-4 py-1.5 text-xs text-muted-foreground"
      >
        <CalendarDays className="size-3.5 shrink-0" />
        <span>
          <span className="font-semibold text-foreground">{t('tracker.convention.title')}</span> {t('tracker.convention.rule')}
          {example ? ` ${t('tracker.convention.example', example)}` : ''}
        </span>
      </div>

      {rangeWarning || notice || gridQuery.isError || !canEdit ? (
        <div className="space-y-2 border-b border-border px-4 py-2">
          {rangeWarning ? (
            <Notice
              tone="danger"
              onDismiss={() => setRangeWarning(null)}
            >
              {rangeWarning}
            </Notice>
          ) : null}
          {gridQuery.isError ? <Notice tone="danger">{serverMessage(gridQuery.error) ?? t('errors.loadFailed')}</Notice> : null}
          {notice ? (
            <Notice
              tone={notice.tone}
              onDismiss={() => setNotice(null)}
            >
              {notice.text}
            </Notice>
          ) : null}
          {!canEdit && grid ? <Notice>{t('tracker.readOnly')}</Notice> : null}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div
          ref={scrollRef}
          className="min-w-0 flex-1 overflow-auto"
          data-tracker-scroll
        >
          <TrackerGrid
            groups={groups}
            weeks={weeks}
            previousWeek={grid?.previousWeek ?? null}
            currentWeekId={grid?.currentWeekId ?? null}
            editing={editing}
            valueAt={valueAt}
            summaryOf={summaryOf}
            targets={grid?.targets ?? {}}
            childrenOf={childrenOf}
            nameOf={nameOf}
            toWeekDate={toWeekDate}
            showComments={showComments}
            onToggleCollapse={(pillar) =>
              setCollapsed((current) => {
                const next = new Set(current);

                if (!next.delete(pillar)) {
                  next.add(pillar);
                }

                return next;
              })
            }
            onOpenComment={onOpenComment}
            empty={search.trim() !== '' ? t('tracker.noMatch', { search: search.trim() }) : t('tracker.noKpis')}
          />
        </div>

        {commentRef && commentKpi && commentWeek ? (
          <CommentPanel
            kpi={commentKpi}
            week={commentWeek}
            record={recordAt(commentRef.kpiId, commentRef.weekId)}
            onClose={() => setCommentRef(null)}
            onApply={(notes) => {
              editing.stageNotes(commentRef, notes);
              setCommentRef(null);
              setNotice({ tone: 'info', text: t('tracker.comments.staged') });
            }}
            onClearValue={() => setDialog({ kind: 'clear-value', ref: commentRef })}
          />
        ) : null}
      </div>

      <footer
        data-status-bar
        className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border bg-card px-4 py-1.5 text-xs text-muted-foreground"
      >
        <span>{t('tracker.status.kpis', { shown: filteredKpis.length, total: grid?.kpis.length ?? 0 })}</span>
        <span>{grid ? editAccessNote(grid.pillars) : ''}</span>
        <span>{t('tracker.status.weeks', { shown: weeks.length, total: allWeeks.length })}</span>
        <span className={unsavedCount > 0 ? 'font-semibold text-primary' : undefined}>
          {unsavedCount > 0 ? t('tracker.status.unsaved', { count: unsavedCount }) : t('tracker.status.allSaved')}
        </span>
        {cellsSelected > 0 ? (
          <span
            data-selection-total
            className="font-mono font-semibold text-foreground"
          >
            {t('tracker.status.selection', { total: selectionTotalText(total), count: cellsSelected })}
          </span>
        ) : null}
        <span className="ms-auto">{t('tracker.status.legend')}</span>
      </footer>

      <Dialog
        isOpen={dialog?.kind === 'discard-then'}
        onClose={() => setDialog(null)}
        title={t('tracker.dialogs.unsavedTitle')}
        description={t('tracker.dialogs.rangeBody', { count: unsavedCount })}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDialog(null)}
            >
              {t('tracker.dialogs.stay')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                const action = dialog?.kind === 'discard-then' ? dialog.action : null;

                editing.discardAll();
                setDialog(null);
                action?.();
              }}
            >
              {t('tracker.dialogs.discardContinue')}
            </Button>
          </>
        }
      />

      <Dialog
        isOpen={dialog?.kind === 'cancel'}
        onClose={() => setDialog(null)}
        title={t('tracker.dialogs.cancelTitle')}
        description={t('tracker.dialogs.cancelBody', { count: unsavedCount })}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDialog(null)}
            >
              {t('tracker.dialogs.keepEditing')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                editing.discardAll();
                setDialog(null);
                setNotice({ tone: 'info', text: t('tracker.discarded') });
              }}
            >
              {t('tracker.dialogs.discard')}
            </Button>
          </>
        }
      />

      <Dialog
        isOpen={dialog?.kind === 'clear-value'}
        onClose={() => setDialog(null)}
        title={t('tracker.dialogs.clearTitle')}
        description={t('tracker.dialogs.clearBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDialog(null)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (dialog?.kind === 'clear-value') {
                  editing.stageClear(dialog.ref);
                }

                setDialog(null);
                setCommentRef(null);
              }}
            >
              {t('tracker.comments.clearValue')}
            </Button>
          </>
        }
      />

      <Dialog
        isOpen={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        title={t('tracker.dialogs.unsavedTitle')}
        description={t('tracker.dialogs.leaveBody', { count: unsavedCount })}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => blocker.reset?.()}
            >
              {t('tracker.dialogs.stay')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                editing.discardAll();
                blocker.proceed?.();
              }}
            >
              {t('tracker.dialogs.discardLeave')}
            </Button>
            <Button
              isLoading={save.isPending}
              onClick={async () => {
                if (await doSave()) {
                  blocker.proceed?.();
                } else {
                  blocker.reset?.();
                }
              }}
            >
              {t('tracker.dialogs.saveLeave')}
            </Button>
          </>
        }
      />
    </div>
  );
};
